import React, { useEffect, useRef, useState } from 'react';
import { IconPlus, IconSend } from './icons';
import { ModelSelector } from './ModelSelector';
import { conversationWorkspaceStrings } from '../../i18n/conversationWorkspace';
import { useTheme } from '../../theme/ThemeProvider';
import { apiClient } from '../../lib/apiClient';

interface ComposerProps {
  disabled: boolean;
  onSend: (text: string, options?: { viaVoice?: boolean }) => void;
  onOpenTools: () => void;
  onOpenConnectors?: () => void;
  onOpenLiveVoice?: () => void;
  modelId: string;
  effortId: string;
  onModelChange: (next: { modelId: string; effortId: string }) => void;
  attachedImageName?: string | null;
  onClearAttachedImage?: () => void;
  // "Create image" was picked from the tools menu -- shows the real
  // generation-size picker (wired to sendChat's size param) instead of a
  // decorative mode indicator.
  imageToolActive?: boolean;
  imageSizeOption?: 'square' | 'portrait' | 'landscape';
  onImageSizeChange?: (option: 'square' | 'portrait' | 'landscape') => void;
  onClearImageTool?: () => void;
  // Opens the real, already-working "new project" flow (the same one the
  // sidebar's Projects section "+" button opens) -- was a dead "#project"
  // anchor with no handler.
  onOpenProject?: () => void;
  // Set by the image viewer's Edit/Redesign/Reframe/Remove-background
  // actions to seed the draft with a prompt and focus the composer -- the
  // same nonce-changed-so-reapply pattern voice dictation would need if it
  // ever had to inject text from outside this component. A new object
  // (even with the same text) always re-applies via its nonce.
  prefillDraft?: { text: string; nonce: number } | null;
  // The "+" tools menu's "Voice" item -- there's no separate voice-message
  // capability anywhere in this codebase (grepped), so it starts the same
  // real dictation the dedicated mic button below already runs, rather than
  // being an inert selection with no visible effect. A new object (even
  // with the same nonce value repeated as a literal) only re-triggers when
  // its identity changes, so bump a counter rather than reusing one object.
  startDictation?: { nonce: number } | null;
}

// Figma nodes 6:159 / 23:257 (composer bar) + 23:256 (disclaimer).
// Only controls with a working user action are rendered.
export function Composer({
  disabled, onSend, onOpenTools, onOpenConnectors = onOpenTools, onOpenLiveVoice, modelId, effortId, onModelChange,
  attachedImageName, onClearAttachedImage, imageToolActive, imageSizeOption = 'square', onImageSizeChange, onClearImageTool,
  onOpenProject,
  prefillDraft,
  startDictation,
}: ComposerProps) {
  const { language } = useTheme();
  const t = (key: string) => conversationWorkspaceStrings[language]?.[key] ?? conversationWorkspaceStrings.en[key] ?? key;
  const [draft, setDraft] = useState('');
  const [fileName, setFileName] = useState('');
  const [dictating, setDictating] = useState(false);
  // A message that started as spoken input gets its reply read back aloud
  // automatically once sent, instead of requiring the manual "Read Aloud"
  // action on the reply. Cleared the moment the user edits the draft by
  // hand, so a message they finished typing themselves is never auto-read.
  const [voiceOrigin, setVoiceOrigin] = useState(false);
  const recognitionRef = useRef<any>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const recorderChunksRef = useRef<Blob[]>([]);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const appliedPrefillNonce = useRef<number | null>(null);
  const appliedDictationNonce = useRef<number | null>(null);

  useEffect(() => () => { try { recognitionRef.current?.stop(); } catch (_) {} try { recorderRef.current?.stop(); } catch (_) {} }, []);

  useEffect(() => {
    if (!prefillDraft || appliedPrefillNonce.current === prefillDraft.nonce) return;
    appliedPrefillNonce.current = prefillDraft.nonce;
    setDraft(prefillDraft.text);
    setVoiceOrigin(false);
    textareaRef.current?.focus();
  }, [prefillDraft]);

  const toggleDictation = () => {
    if (disabled) return;
    const Recognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!Recognition) {
      if (recorderRef.current) { recorderRef.current.stop(); return; }
      navigator.mediaDevices?.getUserMedia({ audio: true }).then((stream) => {
        const recorder = new MediaRecorder(stream);
        recorderRef.current = recorder; recorderChunksRef.current = [];
        recorder.ondataavailable = (event) => { if (event.data.size) recorderChunksRef.current.push(event.data); };
        recorder.onstop = async () => {
          stream.getTracks().forEach((track) => track.stop()); recorderRef.current = null;
          try {
            const blob = new Blob(recorderChunksRef.current, { type: recorder.mimeType || 'audio/webm' });
            const audio = await new Promise<string>((resolve, reject) => { const reader = new FileReader(); reader.onload = () => resolve(String(reader.result).split(',')[1] || ''); reader.onerror = () => reject(reader.error); reader.readAsDataURL(blob); });
            const { data } = await apiClient.call<{ text?: string; transcript?: string }>('/api/ai/transcribe', { body: { audio, mimeType: blob.type, lang: language } });
            const transcript = String(data.text || data.transcript || '').trim();
            if (transcript) { setDraft((prev) => `${prev.trim()}${prev.trim() ? ' ' : ''}${transcript}`); setVoiceOrigin(true); }
            setFileName(transcript ? 'Transcript ready. Review it, then send.' : 'No speech was detected.');
          } catch (_) { setFileName('Voice transcription failed. Check microphone permission and try again.'); }
        };
        recorder.start(); setFileName('Recording… click the microphone again to transcribe.');
      }).catch(() => setFileName('Microphone permission is required for dictation.'));
      return;
    }
    if (dictating) { recognitionRef.current?.stop(); return; }
    const recognition = new Recognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = language === 'am' ? 'am-ET' : language === 'om' ? 'om-ET' : language === 'ar' ? 'ar' : 'en-US';
    const base = draft ? `${draft.trim()} ` : '';
    recognition.onresult = (event: any) => {
      let transcript = '';
      for (let i = 0; i < event.results.length; i += 1) transcript += event.results[i][0]?.transcript || '';
      setDraft(`${base}${transcript}`.trim());
      if (transcript.trim()) setVoiceOrigin(true);
    };
    recognition.onerror = () => { setDictating(false); setFileName('Voice transcription failed. Check microphone permission and try again.'); };
    recognition.onend = () => setDictating(false);
    recognitionRef.current = recognition;
    try { recognition.start(); setDictating(true); setFileName('Listening… review the transcript, then send.'); } catch (_) { setFileName('Could not start voice transcription.'); }
  };

  useEffect(() => {
    if (!startDictation || appliedDictationNonce.current === startDictation.nonce) return;
    appliedDictationNonce.current = startDictation.nonce;
    if (!dictating) toggleDictation();
    // toggleDictation/dictating intentionally excluded -- this should fire
    // only when startDictation itself changes, using whichever closure is
    // current at that render (both are freshly re-created every render).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDictation]);

  const submit = () => {
    const trimmed = draft.trim();
    if (!trimmed || disabled) return;
    onSend(trimmed, { viaVoice: voiceOrigin });
    setDraft('');
    setVoiceOrigin(false);
  };

  return (
    <div className="ozira-cw-composer-wrap">
      <p className="ozira-cw-disclaimer">{t('disclaimer')}</p>
      <form
        className="ozira-cw-composer"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <div className="ozira-cw-composer-prompt" aria-hidden="true">How can I help you today?</div>
        <div className="ozira-cw-composer-controls">
          <button type="button" className="ozira-cw-composer-btn" aria-label={t('toolsTitle')} title={t('toolsTitle')} onClick={onOpenTools} disabled={disabled}>
            <IconPlus />
          </button>
          <button type="button" className="ozira-cw-composer-btn" aria-label="Live voice" title="Live voice" onClick={onOpenLiveVoice} disabled={disabled || !onOpenLiveVoice}>
            <span aria-hidden="true" style={{ fontSize: 15, fontWeight: 600 }}>
              ∞
            </span>
          </button>
          <button type="button" className={`ozira-cw-composer-btn${dictating ? ' is-active' : ''}`} aria-label="Dictate message" title="Dictate message" onClick={toggleDictation} disabled={disabled}>
            <span aria-hidden="true">●</span>
          </button>
        </div>
        <div className="ozira-cw-toolbar-row">
          {/* effortId/modelId are sent on every chat send (sendChat() in
              conversationsApi.ts -> processChatInner in lib/chatservice.js)
              and have real backend effect there -- credit multiplier,
              conversation-history depth, max reply tokens, and the injected
              system-prompt instruction -- so this picker must always be
              visible, not gated behind an internal mode toggle. */}
          <ModelSelector modelId={modelId} effortId={effortId} onChange={onModelChange} disabled={disabled} />
        </div>
        <textarea
          ref={textareaRef}
          className="ozira-cw-composer-input"
          value={draft}
          placeholder={t('messagePlaceholder')}
          aria-label={t('messagePlaceholder')}
          rows={1}
          disabled={disabled}
          onChange={(e) => { setDraft(e.target.value); setVoiceOrigin(false); }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              submit();
            }
          }}
        />
        <button type="submit" className="ozira-cw-send" aria-label={t('send')} title={t('send')} disabled={disabled || !draft.trim()}>
          <IconSend stroke="white" />
        </button>
      </form>
      <nav className="ozira-cw-composer-links" aria-label="Composer resources">
        <button type="button" className="ozira-cw-composer-link" onClick={onOpenProject} disabled={!onOpenProject}>
          {t('project')}
        </button>
        <button
          type="button"
          className="ozira-cw-composer-link"
          onClick={() => setFileName(t('manualUnavailable'))}
        >
          {t('manual')}
        </button>
      </nav>
      {imageToolActive ? (
        <div className="ozira-cw-image-tool-bar" role="group" aria-label="Image generation size">
          <span className="ozira-cw-image-tool-bar__label">Create image</span>
          {(['square', 'portrait', 'landscape'] as const).map((option) => (
            <button
              key={option}
              type="button"
              className={
                imageSizeOption === option
                  ? 'ozira-cw-image-size-chip ozira-cw-image-size-chip--active'
                  : 'ozira-cw-image-size-chip'
              }
              aria-pressed={imageSizeOption === option}
              onClick={() => onImageSizeChange?.(option)}
            >
              {option === 'square' ? 'Square' : option === 'portrait' ? 'Portrait' : 'Landscape'}
            </button>
          ))}
          {onClearImageTool ? (
            <button type="button" className="ozira-cw-upload-status__clear" onClick={onClearImageTool} aria-label="Cancel image generation mode">×</button>
          ) : null}
        </div>
      ) : null}
      {attachedImageName ? (
        <span className="ozira-cw-upload-status" role="status">
          Attached: {attachedImageName}
          {onClearAttachedImage ? (
            <button type="button" className="ozira-cw-upload-status__clear" onClick={onClearAttachedImage} aria-label="Remove attached image">×</button>
          ) : null}
        </span>
      ) : null}
      {fileName ? <span className="ozira-cw-upload-status" role="status">{fileName}</span> : null}
    </div>
  );
}
